import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/** Snackbar types accepted by openSnackBar */
export type WiSnackBarType =
  | 'success-snackbar'
  | 'error-snackbar'
  | 'warning'
  | 'information-snackbar';

/** Snackbar currently displayed by app-wi-snackbar */
export interface WiSnackBar {
  /** Unique per call, so a new message re-creates the snackbar even if the previous one was closed */
  id: number;
  message: string;
  theme: 'success' | 'alert' | 'warning' | 'information';
}

/**
 * Displays a snackbar from anywhere in the app, rendered by app-wi-snackbar (mounted once in AppComponent).
 */
@Injectable({
  providedIn: 'root',
})
export class WiSnackBarService {
  /** Time a snackbar stays displayed, in ms */
  private readonly DURATION = 3000;

  private readonly THEMES: { [type in WiSnackBarType]: WiSnackBar['theme'] } = {
    'success-snackbar': 'success',
    'error-snackbar': 'alert',
    warning: 'warning',
    'information-snackbar': 'information',
  };

  private snackBarSubject = new BehaviorSubject<WiSnackBar | null>(null);
  readonly snackBar$ = this.snackBarSubject.asObservable();

  private nextId = 0;
  private hideTimeout?: ReturnType<typeof setTimeout>;

  /**
   * Displays a snackbar, replacing the current one.
   * @param message - The message to display
   * @param action - Kept for compatibility with existing callers: the snackbar has its own close icon
   * @param type - The snackbar type, which sets its theme
   */
  public openSnackBar(message: string, action: string, type: WiSnackBarType): void {
    clearTimeout(this.hideTimeout);
    this.snackBarSubject.next({ id: ++this.nextId, message, theme: this.THEMES[type] });
    this.hideTimeout = setTimeout(() => this.close(), this.DURATION);
  }

  /** Hides the current snackbar */
  public close(): void {
    clearTimeout(this.hideTimeout);
    this.snackBarSubject.next(null);
  }
}
